import './styles.css'

import { addCommasToNumber, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useMemo, useState } from 'react'
import { useFormatter } from '@api/useFormatter'
import { CreateManualLayerForm } from '@components/InventoryValuation/Costing/Details/CreateManualLayerForm'
import { useMetadata } from '@api/useMetadata'
import { DeliveryRow } from '@components/InventoryValuation/Costing/Details/DeliveryRow'
import { Draws } from '@components/InventoryValuation/Costing/Details/Draws'
import { AdditiveDraws } from './AdditiveDraws'
import type { MiddleLayerProps } from './types'

export const MiddleLayer = ({
  additive_draws,
  sale_layers,
  todays_deliveries,
  inventory_costing_identifier,
  todays_deliveries_totals,
  openOrderModal,
  date,
  showDate,
  expandedRows,
  setExpandedRows,
  scopes,
}: MiddleLayerProps) => {
  const [isAddingLayer, setIsAddingLayer] = useState(false)
  const hasManualLayer = useMemo(
    () => todays_deliveries?.some((d) => !d.order_number && !d.bols.length),
    [todays_deliveries]
  )
  const [totals, setTotals] = useState({ totalVolume: null, totalValue: null })
  const { data: reasonCodes } = useMetadata('valuation/costing')
  const { formatCurrency } = useFormatter()
  const formattedCurrency = formatCurrency(null, true)
  const addBuildLayer = () => {
    setIsAddingLayer(true)
  }
  if (!todays_deliveries && !sale_layers)
    return (
      <Horizontal style={{ width: '100%' }} alignItems={'center'} justifyContent={'center'}>
        <Texto align={'center'} className={'text-size-5 text-gray-500 font-italic'}>
          No Build or Draw Layers
        </Texto>
      </Horizontal>
    )

  return (
    <div className={'full-height-width'}>
      <Horizontal className={'full-height-width'} style={{ width: '100%', minHeight: 'min-content' }}>
        <Vertical className='p-2' flex={1.5} justifyContent={'center'} alignItems={'center'} />
        <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'center'}>
          <Texto appearance='medium'>DEL. DATE</Texto>
        </Vertical>
        <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'center'}>
          <Texto appearance='medium'>BOL</Texto>
        </Vertical>
        <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'center'}>
          <Texto appearance='medium'>ORDER</Texto>
        </Vertical>
        <Vertical className='p-2' flex={1.5} justifyContent={'center'} alignItems={'center'}>
          <Texto appearance='medium'>SUPPLIER</Texto>
        </Vertical>
        <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
          <Texto appearance='medium'>VOLUME</Texto>
        </Vertical>
        <Vertical className='p-2 ' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
          <Texto appearance='medium'>PRICE</Texto>
        </Vertical>
        <Vertical className='p-2 mr-2' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
          <Texto appearance='medium'>VALUE</Texto>
        </Vertical>
      </Horizontal>

      {todays_deliveries?.map((row, i) => {
        return (
          <DeliveryRow
            key={`todays-deliveries-${i}`}
            row={row}
            rowIndex={i}
            openOrderModal={openOrderModal}
            showDate={showDate}
            reasonCodes={reasonCodes}
            inventory_costing_identifier={inventory_costing_identifier}
            setTotals={setTotals}
            totals={totals}
            setExpandedRows={setExpandedRows}
            expandedRows={expandedRows}
            scopes={scopes}
            date={date}
          />
        )
      })}

      <Horizontal
        className='p-2 bordered'
        style={{
          width: '100%',
          background: 'var(--theme-color-2-dim)',
          borderTop: '2px solid var(--theme-color-2)',
        }}
        alignItems={'center'}
      >
        <Vertical className='p-2' flex={1.5} justifyContent={'center'} alignItems={'flex-start'}>
          <Texto className='text-secondary' weight={'bold'}>
            BUILDS
          </Texto>
        </Vertical>
        <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'center'}>
          {!!scopes?.valuation?.write && !hasManualLayer && !isAddingLayer && (
            <GraviButton
              appearance={'ghost'}
              style={{
                borderRadius: 0,
                paddingTop: 0,
                paddingBottom: 0,
                color: 'var(--theme-color-2)',
                height: 'fit-content',
              }}
              buttonText={'Add Layer'}
              onClick={() => addBuildLayer()}
            />
          )}
        </Vertical>
        <Vertical className='p-2' flex={1} />
        <Vertical className='p-2' flex={1} />
        <Vertical className='p-2' flex={1.5} />
        <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
          <Texto className='text-secondary text-truncate' weight={'bold'} align={'right'} style={{ width: '100%' }}>
            {addCommasToNumber(todays_deliveries_totals?.volume, 0)}
          </Texto>
        </Vertical>
        <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
          <Texto className='text-secondary text-truncate' weight={'bold'} align={'right'} style={{ width: '100%' }}>
            {formattedCurrency}
            {addCommasToNumber(todays_deliveries_totals?.price, 4)}
          </Texto>
        </Vertical>
        <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
          <Texto className='text-secondary text-truncate' weight={'bold'} align={'right'} style={{ width: '100%' }}>
            {formattedCurrency}
            {addCommasToNumber(todays_deliveries_totals?.value, 2)}
          </Texto>
        </Vertical>
      </Horizontal>
      {isAddingLayer && (
        <CreateManualLayerForm
          reasonCodes={reasonCodes}
          date={date}
          inventory_costing_identifier={inventory_costing_identifier}
          setIsAddingLayer={setIsAddingLayer}
        />
      )}
      {!!sale_layers?.length && (
        <Draws
          layer={sale_layers[0]}
          openOrderModal={openOrderModal}
          formattedCurrency={formattedCurrency}
          showDate={showDate}
        />
      )}
      <AdditiveDraws draws={additive_draws} formattedCurrency={formattedCurrency} showDate={showDate} />
    </div>
  )
}
